import { z } from "zod";

/**
 * Servicebeauftragungen — geteilte Request-Shapes (FE + BE).
 *
 * Eine Beauftragung ist die Ebene zwischen Kunde und SF-Projekten und trägt
 * den AKTUELLEN Service Lead; Umsätze rechnen über alle zugeordneten Projekte
 * inkl. Historie. Tool-owned: Salesforce kennt Beauftragungen nicht.
 */

/** Body für POST /customers/:id/engagements. */
export const createEngagementSchema = z.object({
  segmentId: z.string().uuid(),
  name: z.string().min(1).max(200),
  leadEmployeeId: z.string().uuid().nullable().optional(),
  deputyEmployeeId: z.string().uuid().nullable().optional(),
});
export type CreateEngagementInput = z.infer<typeof createEngagementSchema>;

/** Body für PATCH /engagements/:id — alles optional, nur Gesetztes ändert. */
export const updateEngagementFieldsSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  leadEmployeeId: z.string().uuid().nullable().optional(),
  deputyEmployeeId: z.string().uuid().nullable().optional(),
  implementerIds: z.array(z.string().uuid()).optional(),
});
export const updateEngagementSchema = updateEngagementFieldsSchema.refine((b) => Object.keys(b).length > 0, {
    message: "Provide at least one field.",
  });
export type UpdateEngagementInput = z.infer<typeof updateEngagementSchema>;

/** Body für PUT /service-projects/:id/engagement — null = bewusst aushängen. */
export const assignProjectEngagementSchema = z.object({
  engagementId: z.string().uuid().nullable(),
});
export type AssignProjectEngagementInput = z.infer<
  typeof assignProjectEngagementSchema
>;

/**
 * Body für POST /service-projects/engagement-bulk — Sammel-Zuordnung.
 * Alle Projekte müssen in derselben (Kunde × Segment)-Zelle liegen;
 * null = Auswahl bewusst aushängen.
 */
export const bulkAssignProjectsSchema = z.object({
  projectIds: z.array(z.string().uuid()).min(1).max(500),
  engagementId: z.string().uuid().nullable(),
});
export type BulkAssignProjectsInput = z.infer<typeof bulkAssignProjectsSchema>;

/** Body für PUT /engagements/:id/targets/:year. */
export const engagementTargetSchema = z.object({
  revenueTargetEur: z.number().nonnegative().nullable(),
  growthTargetPct: z.number().min(-100).max(999).nullable(),
  /**
   * Pflicht, sobald die Zielrunde eingefroren ist — der Server erzwingt es,
   * nicht der Dialog. Mindestens 10 Zeichen: „ok" ist keine Begründung.
   */
  begruendung: z.string().min(10).max(500).optional(),
});
export type EngagementTargetInput = z.infer<typeof engagementTargetSchema>;

export const engagementTargetYearSchema = z.coerce
  .number()
  .int()
  .min(2020)
  .max(2100);
